import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import type { GroupBy, Window } from "../api/console";

export type WindowPreset =
  | "today"
  | "7d"
  | "30d"
  | "90d"
  | "thisMonth"
  | "lastMonth"
  | "thisYear"
  | "custom";

export const WINDOW_PRESETS: WindowPreset[] = [
  "today",
  "7d",
  "30d",
  "90d",
  "thisMonth",
  "lastMonth",
  "thisYear",
  "custom",
];

/** [from, to) for a preset, in the browser's local time (EAT for ops staff). */
export function presetRange(preset: WindowPreset, now = dayjs()): { from: dayjs.Dayjs; to: dayjs.Dayjs } {
  switch (preset) {
    case "today":
      return { from: now.startOf("day"), to: now };
    case "7d":
      return { from: now.subtract(7, "day").startOf("day"), to: now };
    case "90d":
      return { from: now.subtract(90, "day").startOf("day"), to: now };
    case "thisMonth":
      return { from: now.startOf("month"), to: now };
    case "lastMonth": {
      const m = now.subtract(1, "month");
      return { from: m.startOf("month"), to: m.endOf("month").add(1, "millisecond") };
    }
    case "thisYear":
      return { from: now.startOf("year"), to: now };
    case "30d":
    default:
      return { from: now.subtract(30, "day").startOf("day"), to: now };
  }
}

/**
 * Report window kept in the URL (?w=30d or ?from=2026-09-01&to=2026-09-30,
 * &g=week) so a filtered view can be bookmarked and shared.
 */
export function useWindowParam(defaultPreset: WindowPreset = "30d") {
  const [params, setParams] = useSearchParams();
  const preset = (params.get("w") as WindowPreset) || (params.get("from") ? "custom" : defaultPreset);
  const groupBy = (params.get("g") as GroupBy | null) ?? undefined;

  const window: Window & { preset: WindowPreset } = useMemo(() => {
    if (preset === "custom" && params.get("from")) {
      const from = dayjs(params.get("from"));
      const to = params.get("to") ? dayjs(params.get("to")).endOf("day") : dayjs();
      return { preset, from: from.toISOString(), to: to.toISOString(), groupBy };
    }
    const r = presetRange(preset);
    return { preset, from: r.from.toISOString(), to: r.to.toISOString(), groupBy };
  }, [preset, params.get("from"), params.get("to"), groupBy]);

  function update(next: { preset?: WindowPreset; from?: string; to?: string; groupBy?: GroupBy | null }) {
    const p = new URLSearchParams(params);
    if (next.preset && next.preset !== "custom") {
      p.set("w", next.preset);
      p.delete("from");
      p.delete("to");
    }
    if (next.preset === "custom" || next.from) {
      p.delete("w");
      if (next.from) p.set("from", next.from);
      if (next.to) p.set("to", next.to);
    }
    if (next.groupBy === null) p.delete("g");
    else if (next.groupBy) p.set("g", next.groupBy);
    setParams(p, { replace: true });
  }

  return { window, update, params, setParams };
}

/**
 * Day-granularity series come back only for days that had trips; charts need
 * the empty days too, or a quiet Sunday silently disappears from the axis.
 * Weeks/months are returned as-is.
 */
export function fillDays<T extends { bucket: string }>(
  series: T[],
  groupBy: GroupBy | undefined,
  from: string | undefined,
  to: string | undefined,
  empty: (bucket: string) => T,
): T[] {
  if (groupBy !== "day" || !from || !to) return series;
  const byBucket = new Map(series.map((s) => [s.bucket, s]));
  const out: T[] = [];
  let d = dayjs(from).startOf("day");
  const end = dayjs(to);
  while (d.isBefore(end) && out.length < 400) {
    const key = d.format("YYYY-MM-DD");
    out.push(byBucket.get(key) ?? empty(key));
    d = d.add(1, "day");
  }
  return out;
}
